<template>
    <div class="hero is-fullheight">
        <div class="hero-body">
            <div class="container has-text-centered">
                <div class="m-b-lg is-flex is-justify-content-center is-align-items-center is-flex-direction-column">
                    <ollie style="height: 6rem" custom-color="var(--p-primary-500)" />
                    <h1 class="title" style="width: max-content;">Waiting Room</h1>
                </div>
                <div class="is-flex has-content-centered is-flex-direction-column">
                    <div class="message is-flex p-5  is-align-items-center is-justify-content-center">
                        <e-icon icon="info-circle" size="is-medium" class="m-r-sm is-info" />
                        <div class="has-text-black mr-2">
                            {{ messageText }}
                        </div>
                        <spinner :size="24" color="var(--p-primary-500)" />
                    </div>
                    <div class="content is-align-items-start is-flex pt-5 pb-5" style="flex:1">
                        <div
                            v-if="loading"
                            class="is-flex is-align-items-center is-justify-content-center"
                            style="width: 100%;"
                        >
                            <spinner :size="24" color="var(--p-primary-500)" />
                            <span class="ml-2">Loading activity...</span>
                        </div>
                        <div v-else-if="loadError" role="alert" class="tw:w-full tw:text-center">
                            <p>Unable to load activity details. You are still in the waiting room.</p>
                            <e-button data-test="retry-activity" class="tw:mt-3" @click="loadActivity">Try again</e-button>
                        </div>
                        <div
                            v-else-if="!computedActivity"
                            class="is-flex is-align-items-center is-justify-content-center"
                            style="width: 100%;"
                        >
                            <span role="status">Activity details are not available yet. You are still in the waiting room.</span>
                        </div>
                        <ul v-else style="list-style: none;">
                            <li>
                                <e-icon icon="clock" class="is-info" />
                                <span style="font-weight: 500;">Time Limit:</span> {{ timeLimitLabel }}
                            </li>
                            <li>
                                <e-icon icon="list-ul" class="is-info" />
                                <span style="font-weight: 500;">Total Questions:</span> {{ questionCount }}
                            </li>
                            <li>
                                <e-icon icon="web-section-alt" class="is-info" />
                                <span style="font-weight: 500;">Navigation:</span> {{ navigationLocked }}
                            </li>
                            <li>
                                <e-icon icon="bookmark" class="is-info" />
                                <span style="font-weight: 500;">Review:</span> {{ reviewAllowed }}
                            </li>
                            <li>
                                <e-icon icon="calculator" class="is-info" />
                                <span style="font-weight: 500;">Calculator:</span> {{ calculatorEnabled }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Spinner from '@/components/chat/system/Spinner.vue';
import Ollie from '@/components/ollie/Ollie.vue';
import useQuizWaitingRoomActivity from '@/composables/useQuizWaitingRoomActivity.js';
import store from '@/store/Index.js';

export default {
    name: 'QuizStatus',

    components: { Ollie, Spinner },

    props: {
        status: {
            type: [String],
            required: true,
        },

        attemptId: {
            type: [Number, String],
            default: null,
        },

        activityId: {
            type: [Number, String],
            required: false,
            default: null,
        },
    },

    setup() {
        return useQuizWaitingRoomActivity();
    },

    computed: {
        lobby() {
            return this.status ? this.status.includes('waiting-room') : false;
        },

        // Resolve activity id from multiple sources: prop → route → user store.
        // Every source can be absent (e.g. a student moved to the lobby from the
        // dashboard has no route param and no hydrated quiz_activity), so the
        // chain must resolve to null instead of throwing.
        resolvedActivityId() {
            return (
                this.activityId ??
                this.$route?.params?.activityId ??
                store.getters['User/get']('quiz_activity')?.activity?.id ??
                null
            );
        },

        activitySource() {
            // An instructor can move the student from an unrelated activity page.
            // The active attempt is authoritative whenever its id is available.
            return this.attemptId ? { attemptId: this.attemptId } : { activityId: this.resolvedActivityId };
        },

        computedActivity() {
            return this.waitingRoomActivity;
        },

        questionCount() {
            return typeof this.computedActivity?.question_count === 'number'
                ? this.computedActivity.question_count
                : 'N/A';
        },

        content() {
            return this.computedActivity?.activity_content;
        },

        reviewAllowed() {
            return (this.content?.review_allowed ?? false) ? 'Available' : 'Not Available';
        },

        calculatorEnabled() {
            return this.content?.calculator_enabled ? 'Available' : 'Not Available';
        },

        navigationLocked() {
            return this.content?.lockdown ? 'Locked' : 'Unlocked';
        },

        timeLimit() {
            return this.content?.time_limit ? this.content.time_limit / 60 : null;
        },

        timeLimitLabel() {
            return this.timeLimit ? `${this.timeLimit} min` : 'No Time Limit';
        },

        messageText() {
            return this.status.includes('post')
                ? 'Please wait, an instructor will review your quiz shortly.'
                : 'Please wait, an instructor will begin your next step shortly.';
        },
    },

    watch: {
        activitySource: {
            immediate: true,
            handler() {
                this.loadActivity();
            },
        },
    },

    methods: {
        loadActivity() {
            return this.loadWaitingRoomActivity(this.activitySource);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';

.hero {
    background-color: $white;
}

.message {
    background-color: var(--primary--light-color);
    color: black;
    border-radius: $radius;
    border-left: 4px solid $primary;
}

.content {
    text-align: left;
    border: 1px solid var(--primary-light-color);
    border-radius: $radius;
    width: 45%;

   li{
        display: flex;
        align-items: center;
        justify-content: start;
        gap: 8px;
    }

}
</style>
